import { z } from 'zod';

import { conversationStatusSchema } from '../enums';
import { cursorPaginationSchema } from './pagination';

/**
 * Фильтры списка диалогов.
 *
 * Схемы рассчитаны на query-строку: булево приезжает как "true", а повторённый
 * параметр (`tagIds=a&tagIds=b`) — то массивом, то одиночной строкой, в
 * зависимости от того, сколько значений выбрал оператор. Нормализуем здесь,
 * чтобы каждый обработчик не разбирался с этим заново.
 */

/** `true`, `1`, `yes`, `on` — и настоящий boolean из JSON-тела. */
const queryBool = z.union([z.boolean(), z.stringbool()]);

/** Одно значение или список — на выходе всегда список. */
function queryArray<T extends z.ZodType>(item: T) {
  return z
    .union([item, z.array(item)])
    .transform((value) => (Array.isArray(value) ? value : [value]));
}

/**
 * Спецзначения фильтра по исполнителю. Строки, а не uuid: «мои» вычисляются от
 * текущего пользователя на сервере — клиент не должен подставлять чужой id и
 * получать чужую выборку.
 */
export const ASSIGNEE_ME = 'me';
export const ASSIGNEE_UNASSIGNED = 'unassigned';

export const assigneeFilterSchema = z.union([
  z.literal(ASSIGNEE_ME),
  z.literal(ASSIGNEE_UNASSIGNED),
  z.uuid(),
]);
export type AssigneeFilter = z.infer<typeof assigneeFilterSchema>;

/** Как сочетать теги: любой из выбранных или все сразу. */
export const TAG_MATCH_MODES = ['any', 'all'] as const;
export const tagMatchModeSchema = z.enum(TAG_MATCH_MODES).default('any');
export type TagMatchMode = (typeof TAG_MATCH_MODES)[number];

/**
 * Архив — третье состояние, а не флаг: список по умолчанию его прячет, папка
 * «Архив» показывает только его, а глобальный поиск обязан видеть оба.
 */
export const ARCHIVE_FILTERS = ['exclude', 'only', 'all'] as const;
export const archiveFilterSchema = z.enum(ARCHIVE_FILTERS).default('exclude');
export type ArchiveFilter = (typeof ARCHIVE_FILTERS)[number];

export const CONVERSATION_SORTS = ['lastMessageAt', 'createdAt', 'oldestWaiting'] as const;
export const conversationSortSchema = z.enum(CONVERSATION_SORTS).default('lastMessageAt');
export type ConversationSort = (typeof CONVERSATION_SORTS)[number];

export const CONVERSATION_SEARCH_MAX = 200;

export const conversationFiltersSchema = z.object({
  /** Не задан — все проекты, к которым у оператора есть membership. */
  projectId: z.uuid().optional(),
  /** Полезен, когда у проекта несколько саппорт-ботов. */
  botId: z.uuid().optional(),
  status: queryArray(conversationStatusSchema).default([]),
  assignee: assigneeFilterSchema.optional(),
  tagIds: queryArray(z.uuid()).default([]),
  tagMatch: tagMatchModeSchema,
  /** Папка — сохранённый набор тегов; раскрывается в tagIds на сервере. */
  folderId: z.uuid().optional(),
  /** Имя, ник, телефон, текст сообщений. */
  search: z.string().trim().min(1).max(CONVERSATION_SEARCH_MAX).optional(),
  unreadOnly: queryBool.default(false),
  archived: archiveFilterSchema,
  sort: conversationSortSchema,
});
export type ConversationFilters = z.infer<typeof conversationFiltersSchema>;
export type ConversationFiltersInput = z.input<typeof conversationFiltersSchema>;

/** Фильтры плюс курсор — то, что реально принимает GET /conversations. */
export const conversationListQuerySchema = conversationFiltersSchema.extend(
  cursorPaginationSchema.shape,
);
export type ConversationListQuery = z.infer<typeof conversationListQuerySchema>;
export type ConversationListQueryInput = z.input<typeof conversationListQuerySchema>;

/** Пустые фильтры: состояние по умолчанию для стора на фронте. */
export const DEFAULT_CONVERSATION_FILTERS: ConversationFilters =
  conversationFiltersSchema.parse({});

/**
 * Есть ли хоть один активный фильтр — по этому решается, показывать ли кнопку
 * «сбросить». projectId и sort не в счёт: это не фильтрация, а навигация.
 */
export function hasActiveFilters(filters: ConversationFilters): boolean {
  return (
    filters.status.length > 0 ||
    filters.assignee !== undefined ||
    filters.tagIds.length > 0 ||
    filters.folderId !== undefined ||
    filters.search !== undefined ||
    filters.unreadOnly ||
    filters.archived !== 'exclude'
  );
}
